import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Swap from '../../common/icons/Swap';
import Copy from '../../common/icons/Copy';
import { colors } from '../../../utils/utils';
import { BASE_URL } from '../../../utils/config';
import { useAppSelector } from '../../../store/hooks';
import { getAuthToken } from '../../../store/selectors/auth';
import type { RootState } from '../../../store/types';

interface LanguageOption {
    code: string;
    apiName: string;
    label: string;
}

interface PersistedTranslatorStateV1 {
    v: 1;
    sourceLang: string;
    targetLang: string;
    sourceText: string;
    translatedText: string;
}

interface TranslatorErrorLike {
    code?: string;
    message?: string;
    response?: { status?: number };
}

type LocalizationParams = Record<string, string | number>;

const LS_KEY = 'i2x-coach:translatorState';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPersistedTranslatorState(value: unknown): value is PersistedTranslatorStateV1 {
    return (
        isRecord(value) &&
        value.v === 1 &&
        typeof value.sourceLang === 'string' &&
        typeof value.targetLang === 'string' &&
        typeof value.sourceText === 'string' &&
        typeof value.translatedText === 'string'
    );
}

function toTranslatorErrorLike(error: unknown): TranslatorErrorLike | null {
    return isRecord(error) ? (error as TranslatorErrorLike) : null;
}

function safeLoadTranslatorState(): PersistedTranslatorStateV1 | null {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isPersistedTranslatorState(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function safeSaveTranslatorState(state: PersistedTranslatorStateV1) {
    try {
        window.localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch {
        // ignore
    }
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #1f1f1f;
`;

const LanguageBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 8px 0;
    border-bottom: 1px solid #e8e8e8;

    @media (max-width: 840px) {
        gap: 6px;
    }
`;

const PanelGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media (max-width: 840px) {
        grid-template-columns: 1fr;
    }
`;

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fff;

    &:first-child {
        margin-right: 0;
    }
`;

const PanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #777;
    border-bottom: 1px solid #f0f0f0;
`;

const Select = styled.select`
    width: auto;
    flex: 1 1 0;
    min-width: 0;
    height: 36px;
    padding: 0 10px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.16);
    background: #fff;
    font-size: 13px;
`;

const TextArea = styled.textarea`
    width: 100%;
    resize: none;
    overflow: hidden;
    border: 0;
    outline: none;
    padding: 12px;
    font-size: 14px;
    line-height: 1.45;
    background: transparent;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const IconButton = styled.button`
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    background: #fff;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
`;

const TranslateButton = styled.button`
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    border: 0;
    background: #5d5ff9;
    color: #fff;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
`;

const SwapButton = styled.button`
    height: 36px;
    min-width: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    border: 1px solid #d9d9d9;
    background: #fff;
    cursor: pointer;

    &:hover {
        background: #f5f6ff;
    }

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }

    @media (max-width: 840px) {
        flex-shrink: 0;
    }
`;

const Hint = styled.div`
    font-size: 12px;
    color: #777;
`;

const CopyIconWrapper = styled.span`
    padding-top: 2px;
    display: inline-block;
`;

const translatorHttp = axios.create({
    timeout: 15000,
    validateStatus: () => true
});

interface TranslatorResponsePayload {
    translatedText?: string;
}

function toTranslatorResponsePayload(value: unknown): TranslatorResponsePayload | null {
    return isRecord(value) ? { translatedText: typeof value.translatedText === 'string' ? value.translatedText : undefined } : null;
}

function humanizeTranslateError(error: unknown, fallbackMessage: string, serverUnavailableMessage: string): string {
    const maybeError = toTranslatorErrorLike(error);
    if (!maybeError) return fallbackMessage;

    const status = maybeError.response?.status;
    if (typeof status === 'number' && status >= 500) {
        return serverUnavailableMessage;
    }

    if (maybeError.code === 'ERR_NETWORK' || maybeError.code === 'ECONNABORTED') {
        return serverUnavailableMessage;
    }

    if (typeof maybeError.message === 'string') {
        const statusMatch = maybeError.message.match(/\((\d{3})\)/);
        const statusCode = statusMatch ? Number(statusMatch[1]) : NaN;
        if (!Number.isNaN(statusCode) && statusCode >= 500) {
            return serverUnavailableMessage;
        }
    }

    return maybeError.message || fallbackMessage;
}

async function translateText({
    text,
    sourceLanguage,
    targetLanguage,
    token
}: {
    text: string;
    sourceLanguage: string;
    targetLanguage: string;
    token: string;
}): Promise<string> {
    const endpoint = `${BASE_URL}/ai/v1/translate`;

    const body = {
        text,
        targetLanguage,
        sourceLanguage,
        context: 'Trainer translator'
    };

    const res = await translatorHttp.post(endpoint, body, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    if (res.status < 200 || res.status >= 300) {
        throw new Error(`Translation request failed (${res.status})`);
    }

    const payload = toTranslatorResponsePayload(res.data);
    const content = typeof payload?.translatedText === 'string' ? payload.translatedText : null;

    if (typeof content !== 'string') {
        throw new Error('Translation response format is invalid');
    }

    return content.trim();
}

interface LocalizationLike {
    getText: (key: string, options?: LocalizationParams) => string;
}

export function Translator({ localization }: { localization: LocalizationLike }) {
    const authToken = useAppSelector((state: RootState) => getAuthToken(state) || '');
    const languageOptions: LanguageOption[] = useMemo(
        () => [
            { code: 'en', apiName: 'English', label: localization.getText('translator.language.en') },
            { code: 'fr', apiName: 'French', label: localization.getText('translator.language.fr') },
            { code: 'es', apiName: 'Spanish', label: localization.getText('translator.language.es') },
            { code: 'it', apiName: 'Italian', label: localization.getText('translator.language.it') },
            { code: 'pl', apiName: 'Polish', label: localization.getText('translator.language.pl') },
            { code: 'tr', apiName: 'Turkish', label: localization.getText('translator.language.tr') },
            { code: 'de', apiName: 'German', label: localization.getText('translator.language.de') }
        ],
        [localization]
    );

    const persisted = useMemo(() => safeLoadTranslatorState(), []);

    const [sourceLang, setSourceLang] = useState<string>(persisted?.sourceLang ?? 'en');
    const [targetLang, setTargetLang] = useState<string>(persisted?.targetLang ?? 'de');
    const [sourceText, setSourceText] = useState<string>(persisted?.sourceText ?? '');
    const [translatedText, setTranslatedText] = useState<string>(persisted?.translatedText ?? '');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const requestIdRef = useRef(0);
    const sourceTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const translatedTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const syncTextAreaHeight = useCallback((node: HTMLTextAreaElement | null) => {
        if (!node) return;
        node.style.height = 'auto';
        node.style.height = `${node.scrollHeight}px`;
    }, []);

    const targetLangLabel = useMemo(() => {
        return languageOptions.find(o => o.code === targetLang)?.label ?? targetLang;
    }, [languageOptions, targetLang]);
    const sourceLangApiName = useMemo(() => {
        return languageOptions.find(o => o.code === sourceLang)?.apiName ?? sourceLang;
    }, [languageOptions, sourceLang]);
    const targetLangApiName = useMemo(() => {
        return languageOptions.find(o => o.code === targetLang)?.apiName ?? targetLang;
    }, [languageOptions, targetLang]);

    const doCopy = useCallback(async (text: string) => {
        if (!text) return;
        await navigator.clipboard.writeText(text);
    }, []);

    const runTranslation = useCallback(
        async (sourceLanguageName?: string, targetLanguageName?: string) => {
            const trimmed = sourceText.trim();
            if (!trimmed) return;

            const requestId = ++requestIdRef.current;
            setIsLoading(true);
            setError(null);

            try {
                if (!authToken) {
                    throw new Error(localization.getText('translator.translation_failed_error'));
                }
                const out = await translateText({
                    text: sourceText,
                    sourceLanguage: sourceLanguageName ?? sourceLangApiName,
                    targetLanguage: targetLanguageName ?? targetLangApiName,
                    token: authToken
                });
                if (requestIdRef.current !== requestId) return;
                setTranslatedText(out);
            } catch (e: unknown) {
                if (requestIdRef.current !== requestId) return;
                setError(
                    humanizeTranslateError(
                        e,
                        localization.getText('translator.translation_failed_error'),
                        localization.getText('translator.server_unavailable_error')
                    )
                );
            } finally {
                if (requestIdRef.current === requestId) setIsLoading(false);
            }
        },
        [sourceText, sourceLangApiName, targetLangApiName, authToken, localization]
    );

    const swapPanels = useCallback(() => {
        if (!translatedText) return;

        requestIdRef.current += 1;
        setError(null);
        setIsLoading(false);

        const newSourceLang = targetLang;
        const newTargetLang = sourceLang;

        setSourceLang(newSourceLang);
        setTargetLang(newTargetLang);
        setSourceText(translatedText);
        setTranslatedText(sourceText);

        const newSourceLangApiName = languageOptions.find(o => o.code === newSourceLang)?.apiName ?? newSourceLang;
        const newTargetLangApiName = languageOptions.find(o => o.code === newTargetLang)?.apiName ?? newTargetLang;

        runTranslation(newSourceLangApiName, newTargetLangApiName);
    }, [sourceLang, targetLang, sourceText, translatedText, languageOptions, runTranslation]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            safeSaveTranslatorState({
                v: 1,
                sourceLang,
                targetLang,
                sourceText,
                translatedText
            });
        }, 200);

        return () => window.clearTimeout(handle);
    }, [sourceLang, targetLang, sourceText, translatedText]);

    useEffect(() => {
        const trimmed = sourceText.trim();
        if (!trimmed) {
            setTranslatedText('');
            setError(null);
            return;
        }

        const handle = window.setTimeout(async () => {
            const currentSourceLangApiName = languageOptions.find(o => o.code === sourceLang)?.apiName ?? sourceLang;
            const currentTargetLangApiName = languageOptions.find(o => o.code === targetLang)?.apiName ?? targetLang;
            await runTranslation(currentSourceLangApiName, currentTargetLangApiName);
        }, 450);

        return () => window.clearTimeout(handle);
    }, [sourceText, sourceLang, targetLang, runTranslation, languageOptions]);

    useEffect(() => {
        syncTextAreaHeight(sourceTextAreaRef.current);
    }, [sourceText, syncTextAreaHeight]);

    useEffect(() => {
        syncTextAreaHeight(translatedTextAreaRef.current);
    }, [translatedText, syncTextAreaHeight]);

    return (
        <Container>
            <TitleBlock>
                <Title>{localization.getText('tabs.translator')}</Title>
            </TitleBlock>
            <LanguageBar>
                <Select
                    value={sourceLang}
                    onChange={e => setSourceLang(e.target.value)}
                    aria-label={localization.getText('translator.from_label')}>
                    {languageOptions.map(opt => (
                        <option key={opt.code} value={opt.code}>
                            {opt.label}
                        </option>
                    ))}
                </Select>

                <SwapButton
                    title={localization.getText('translator.swap_panels_button_text')}
                    onClick={swapPanels}
                    disabled={!translatedText}>
                    <Swap size={16} color={colors.blue.palatinate_blue} />
                </SwapButton>

                <Select
                    value={targetLang}
                    onChange={e => setTargetLang(e.target.value)}
                    aria-label={localization.getText('translator.to_with_language_label', { language: targetLangLabel })}>
                    {languageOptions.map(opt => (
                        <option key={opt.code} value={opt.code}>
                            {opt.label}
                        </option>
                    ))}
                </Select>
            </LanguageBar>

            <PanelGrid>
                <Panel>
                    <PanelHeader>
                        <div>{localization.getText('translator.from_label')}</div>
                        <Actions>
                            <IconButton
                                title={localization.getText('translator.copy_button_title')}
                                onClick={() => doCopy(sourceText)}
                                disabled={!sourceText}>
                                <CopyIconWrapper>
                                    <Copy size={13} color={colors.blue.palatinate_blue} />
                                </CopyIconWrapper>
                            </IconButton>
                        </Actions>
                    </PanelHeader>

                    <TextArea
                        ref={sourceTextAreaRef}
                        value={sourceText}
                        onChange={e => {
                            setSourceText(e.target.value);
                            syncTextAreaHeight(e.target);
                        }}
                        placeholder={localization.getText('translator.source_placeholder')}
                    />
                </Panel>

                <Panel>
                    <PanelHeader>
                        <div>{localization.getText('translator.to_with_language_label', { language: targetLangLabel })}</div>
                        <Actions>
                            <IconButton
                                title={localization.getText('translator.copy_button_title')}
                                onClick={() => doCopy(translatedText)}
                                disabled={!translatedText}>
                                <CopyIconWrapper>
                                    <Copy size={13} color={colors.blue.palatinate_blue} />
                                </CopyIconWrapper>
                            </IconButton>

                            <TranslateButton
                                onClick={() => runTranslation(sourceLangApiName, targetLangApiName)}
                                disabled={!sourceText.trim()}>
                                {localization.getText('translator.translate_button_text')}
                            </TranslateButton>
                        </Actions>
                    </PanelHeader>

                    <TextArea
                        ref={translatedTextAreaRef}
                        value={translatedText}
                        readOnly
                        placeholder={localization.getText('translator.translation_placeholder', { language: targetLangLabel })}
                    />
                </Panel>
            </PanelGrid>

            <Hint>
                {isLoading
                    ? localization.getText('translator.hint_translating')
                    : error
                      ? localization.getText('translator.hint_error', { error })
                      : localization.getText('translator.hint_tip')}
            </Hint>
        </Container>
    );
}
